<template>
    <div>
        <div class="mb-3 flex justify-end gap-x-5">
            <hts-form-button
                v-if="checkPermission(layoutPermissions['costing_costing_add'])"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="openAddCosting">
                {{ $t('labels.add') }}
            </hts-form-button>
        </div>
        <hts-data-table
            ref="dataTables"
            :headers="headers()"
            :server="apiListCooperator"
            use-selection
            :use-archive="useCostingArchive"
            :use-restore="useCostingRestore"
            store-name="costDataTable"
            store-key="Cost" />
        <add-costing
            ref="addCosting"
            @refreshTables="refreshTables" />
        <edit-costing
            ref="editCosting"
            @refreshTables="refreshTables" />
        <hts-modal
            :show="showPopupDeleteCosting"
            :title="$t('labels.deleteCosting')"
            type="confirm"
            :content="$t('labels.confirmDeleteCosting')"
            @hide="closePopupDeleteAssumption"
            @confirm="handleDeleteCosting" />
    </div>
</template>

<script setup lang="ts">
import AddCosting from './-component/assumption-costing/add.vue'
import EditCosting from './-component/assumption-costing/edit.vue'
import { $api, $url } from '#imports'
import layoutPermissions from '@/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
import type { IDataCosting } from '~/pages/trucker-[id]/cost/-component/imports/form'
import { numberFormatter } from '~/pages/trucker-[id]/price/vendor/ignore/data'

import actions from '~/pages/trucker-[id]/partner/-components/actions.vue'

const { $formatters } = useNuxtApp()

const apiListCooperator = ref({ ...$url.cost.costing.costingList })

const addCosting: any = ref(null)
const openAddCosting = () => {
  if (addCosting.value) {
    addCosting.value.open()
  }
}

const costingId = ref('')
const editCosting: any = ref(null)
const vehicleCategories = ref<null | Array<any>>()

onBeforeMount(() => {
  const api = $urlAdmin.vehicle.category.getAll

  $api(api).then((data) => {
    const resData: any = data?.data?.value

    if (resData?.success) {
      vehicleCategories.value = resData.data || []
    }
  })
})

const openEditCosting = (data: IDataCosting) => {
  if (editCosting.value) {
    editCosting.value.open(data?._id)
  }
}

const useCostingArchive = computed(() => {
  return { api: $url.cost.costing.archiveCosting, permissions: checkPermission(layoutPermissions.costing_costing_archive) }
})
const useCostingRestore = computed(() => {
  return { api: $url.cost.costing.restoreCosting, permissions: checkPermission(layoutPermissions.costing_costing_restore) }
})

const showPopupDeleteCosting = ref(false)
const openDeleteAssumption = (data: IDataCosting) => {
  costingId.value = data?._id
  showPopupDeleteCosting.value = true
}
const closePopupDeleteAssumption = () => {
  showPopupDeleteCosting.value = false
  costingId.value = ''
}
const contTypes = [
  {
    key: '20F',
    title: $t('labels.20f')
  },
  {
    key: '40F',
    title: $t('labels.40f')
  },
  {
    key: '45F',
    title: $t('labels.45f')
  }
]

const handleDeleteCosting = () => {
  const api = $url.cost.costing.deleteCosting
  $api(api, { uriParams: { ID: costingId.value } })
    .then((data: any) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (responseData.success) {
        process.client && $toast().success($t('labels.deleteCostingSuccess'))
        costingId.value = ''
      }
      refreshTables()
      showPopupDeleteCosting.value = false
    })
    .catch(() => {
      showPopupDeleteCosting.value = false
      costingId.value = ''
    })
}

const headers = () => {
  let headersDefault: any = [
    {
      headerName: 'Chi phí',
      marryChildren: true,
      children: [
        {
          field: 'name',
          headerName: $t('labels.costingName'),
          sortable: true,
          filter: 'agTextColumnFilter'
        },
        {
          field: 'name',
          headerName: $t('labels.typeCommodity'),
          valueGetter: (params: any) => {
            if (params.data.typeCommodity === 'NORMAL') {
              return $t('labels.ordinaryGoods')
            } else if (params.data.typeCommodity === 'FROZEN') {
              return $t('labels.frozen')
            } else if (params.data.typeCommodity === 'DANGEROUS') {
              return $t('labels.dangerousGoods')
            } else if (params.data.typeCommodity === 'FRAGILE') {
              return $t('labels.fragileGoods')
            } else if (params.data.typeCommodity === 'LIQUID') {
              return $t('labels.liquid')
            } else if (params.data.typeCommodity === 'GAS') {
              return $t('labels.gas')
            } else if (params.data.typeCommodity === 'OVERWEIGHT') {
              return $t('labels.overweightCargo')
            }

            return ''
          }
        }
      ]
    }
  ]

  const headersRemooc: any = [
    {
      headerName: $t('labels.mooc'),
      marryChildren: true,
      children: [
        {
          field: 'costs',
          headerName: $t('labels.calculatedType2'),
          valueGetter: (params: any) => {
            if (!params.data.targets?.includes('REMOOC')) {
              return ''
            }

            const type = params.data.prices?.REMOOC?.calculatedType
            if (type === 'MONTH') {
              return $t('labels.month')
            } else if (type === 'TRIP') {
              return $t('labels.journey')
            } else if (type === 'KM') {
              return $t('labels.km')
            }

            return ''
          }
        },
        ...contTypes.map((type) => {
          return {
            headerName: type.title,
            sortable: false,
            valueGetter: (params: any) => {
              if (!params.data.targets?.includes('REMOOC')) {
                return ''
              }
              const amount = params.data.prices?.REMOOC?.prices?.[type.key]?.amount
              return typeof amount === 'number' ? numberFormatter(amount) : ''
            }
          }
        })
      ]
    }
  ]

  const headersCont: any = [
    {
      headerName: $t('labels.supplyList.container'),
      marryChildren: true,
      children: [
        {
          field: 'prices',
          headerName: $t('labels.calculatedType2'),
          valueGetter: (params: any) => {
            if (!params.data.targets?.includes('CONT')) {
              return ''
            }

            const type = params.data.prices?.CONT?.calculatedType
            if (type === 'MONTH') {
              return $t('labels.month')
            } else if (type === 'TRIP') {
              return $t('labels.journey')
            } else if (type === 'KM') {
              return $t('labels.km')
            }

            return ''
          }
        },
        ...contTypes.map((type) => {
          return {
            headerName: type.title,
            sortable: false,
            valueGetter: (params: any) => {
              if (!params.data.targets?.includes('CONT')) {
                return ''
              }
              const amount = params.data.prices?.CONT?.prices?.[type.key]?.amount
              return typeof amount === 'number' ? numberFormatter(amount) : ''
            }
          }
        })
      ]
    }
  ]

  const headersVehicle = () => {
    if (!vehicleCategories.value) {
      return []
    }
    return [
      {
        headerName: $t('labels.vehicle'),
        marryChildren: true,
        children: [
          {
            field: 'prices',
            headerName: $t('labels.calculatedType2'),
            valueGetter: (params: any) => {
              if (!params.data.targets?.includes('VEHICLE')) {
                return ''
              }

              const type = params.data.prices?.VEHICLE?.calculatedType
              if (type === 'MONTH') {
                return $t('labels.month')
              } else if (type === 'TRIP') {
                return $t('labels.journey')
              } else if (type === 'KM') {
                return $t('labels.km')
              }

              return ''
            }
          },
          ...(vehicleCategories?.value?.map((item: any) => {
            const id = item._id
            return {
              headerName: item.name,
              sortable: false,
              valueGetter: (params: any) => {
                if (!params.data.targets?.includes('VEHICLE')) {
                  return ''
                }
                const amount = params.data.prices?.VEHICLE?.prices?.[id]?.amount
                return typeof amount === 'number' ? numberFormatter(amount) : ''
              }
            }
          }) || [])
        ]
      }
    ]
  }

  const headerTimes = {
    headerName: $t('labels.date'),
    marryChildren: true,
    children: [
      {
        field: 'createdAt',
        headerName: $t('labels.createdAt'),
        sortable: true,
        minWidth: 200,
        valueGetter: (params: any) => {
          if (params.data.createdAt) {
            return $formatters.datetime(params.data.createdAt)
          }
        }
      },
      {
        field: 'updatedAt',
        headerName: $t('labels.updatedAt'),
        sortable: true,
        minWidth: 200,
        valueGetter: (params: any) => {
          if (params.data.updatedAt) {
            return $formatters.datetime(params.data.updatedAt)
          }
        }
      }
    ]
  }

  const headersAction = [
    {
      field: 'action',
      pinned: 'right',
      sortable: false,
      lockPosition: true,
      lockPinned: true,
      popup: true,
      suppressRowTransform: true,
      headerName: $t('labels.action'),
      cellStyle: { overflow: 'visible' },
      cellRenderer: actions,
      cellRendererParams: {
        storeKey: 'costDataTable',
        storeName: 'Cost',
        items: [
          {
            icon: 'hi-pencil-square-icon',
            label: $t('labels.edit'),
            class: 'text-blue-600 hover:text-blue-500',
            permissions: checkPermission(layoutPermissions.costing_costing_edit),
            click: openEditCosting
          },
          {
            icon: 'hi-trash-icon',
            label: $t('labels.delete'),
            class: 'text-error-600 hover:text-error-500',
            permissions: checkPermission(layoutPermissions.costing_costing_delete),
            isArchive: true,
            click: openDeleteAssumption
          }
        ]
      }
    }
  ]

  if (Array.isArray(headersRemooc) && Array.isArray(headersDefault)) {
    headersDefault = headersDefault.concat(headersRemooc)
  }

  if (Array.isArray(headersCont) && Array.isArray(headersDefault)) {
    headersDefault = headersDefault.concat(headersCont)
  }

  if (Array.isArray(headersVehicle()) && Array.isArray(headersDefault)) {
    headersDefault = headersDefault.concat(headersVehicle())
  }

  headersDefault = headersDefault.concat(headerTimes)
  headersDefault = headersDefault.concat(headersAction)

  return headersDefault
}

const dataTables = ref()

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}
</script>
